import type { RecoveryConfig } from './types';

export const DEFAULT_RECOVERY_CONFIG: Readonly<RecoveryConfig> = Object.freeze({
  signalingInitialBackoffMs: 500,
  iceInitialBackoffMs: 750,
  maximumBackoffMs: 10_000,
  backoffMultiplier: 2,
  jitterRatio: 0.2,
  maximumSignalingAttempts: 6,
  maximumIceRestartAttempts: 4,
  disconnectedGraceMs: 3_000,
  networkChangeGraceMs: 1_000,
  iceRestartTimeoutMs: 8_000,
  relayFallbackAttempt: 2,
  packetLossDegradedPercentage: 8,
  packetLossDegradedSamples: 2,
  packetLossRecoveryPercentage: 2,
  packetLossRecoverySamples: 3,
});

export function createRecoveryConfig(
  overrides: Partial<RecoveryConfig> = {},
): Readonly<RecoveryConfig> {
  const config = Object.freeze({ ...DEFAULT_RECOVERY_CONFIG, ...overrides });
  validateRecoveryConfig(config);
  return config;
}

export function validateRecoveryConfig(config: RecoveryConfig): void {
  const durations = [
    config.signalingInitialBackoffMs,
    config.iceInitialBackoffMs,
    config.maximumBackoffMs,
    config.disconnectedGraceMs,
    config.networkChangeGraceMs,
    config.iceRestartTimeoutMs,
  ];
  if (durations.some((value) => !Number.isFinite(value) || value < 0)) {
    throw new TypeError('recovery durations must be non-negative finite numbers');
  }
  if (
    config.maximumBackoffMs < Math.max(config.signalingInitialBackoffMs, config.iceInitialBackoffMs)
  ) {
    throw new TypeError('maximumBackoffMs cannot be lower than an initial backoff');
  }
  const counts = [
    config.maximumSignalingAttempts,
    config.maximumIceRestartAttempts,
    config.relayFallbackAttempt,
    config.packetLossDegradedSamples,
    config.packetLossRecoverySamples,
  ];
  if (counts.some((value) => !Number.isInteger(value) || value < 1)) {
    throw new TypeError('recovery attempt and sample counts must be positive integers');
  }
  if (config.relayFallbackAttempt > config.maximumIceRestartAttempts) {
    throw new TypeError('relay fallback must occur within the ICE restart attempt limit');
  }
  if (!Number.isFinite(config.backoffMultiplier) || config.backoffMultiplier < 1) {
    throw new TypeError('backoffMultiplier must be at least 1');
  }
  if (!Number.isFinite(config.jitterRatio) || config.jitterRatio < 0 || config.jitterRatio > 1) {
    throw new TypeError('jitterRatio must be between 0 and 1');
  }
  if (
    !Number.isFinite(config.packetLossRecoveryPercentage) ||
    !Number.isFinite(config.packetLossDegradedPercentage) ||
    config.packetLossRecoveryPercentage < 0 ||
    config.packetLossDegradedPercentage > 100 ||
    config.packetLossRecoveryPercentage > config.packetLossDegradedPercentage
  ) {
    throw new TypeError('packet-loss thresholds must be ordered recovery then degraded');
  }
}

export function exponentialBackoff(
  attempt: number,
  initialMs: number,
  config: Pick<RecoveryConfig, 'maximumBackoffMs' | 'backoffMultiplier' | 'jitterRatio'>,
  random: () => number,
): number {
  const boundedAttempt = Math.max(1, attempt);
  const base = Math.min(
    config.maximumBackoffMs,
    initialMs * config.backoffMultiplier ** (boundedAttempt - 1),
  );
  const centeredRandom = Math.min(1, Math.max(0, random())) * 2 - 1;
  const jittered = base * (1 + centeredRandom * config.jitterRatio);
  return Math.max(0, Math.min(config.maximumBackoffMs, Math.round(jittered)));
}
